import { useContext, useEffect, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import { MediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import Header from '@/components/header/Header';
import CheckoutSessionsPaymentForm from '@/components/purchase/CheckoutSessionsPaymentForm';
import { useSimplifiedSignupContext } from '@/components/SimplifiedSignup/Context';
import usePurchaseBodyArgs from '@/components/Sisu/hooks/usePurchaseBodyArgs';
import { useAnalytics } from '@/hooks/analytics';

import { ShareContext } from '../ShareProvider';
import { SHARE_FREE_CONTENT_FLOW_NAME } from './constants';
import messages from './messages';
import { Container, LimitedTimeCallout, PaymentSubHeading, Wrapper } from './styles';

function SharePayment(): JSX.Element {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const { playerMode, onPlay } = useContext(MediaPlayerContext);
	// AppLayout's provider carries its client enrollment read, which has resolved by the time a
	// buyer opens this screen. The form below mounts fresh on open and latches that value once.
	const { isCheckoutSessionsEnabled } = useSimplifiedSignupContext();
	// The heading and the purchase body read one trial flag, so the copy matches what is charged.
	const { freeTrial: isFreeTrial } = usePurchaseBodyArgs({});

	const { shouldShowSharePayment, onUpsellExit: _onUpsellExit, onPurchaseSuccess } = useContext(ShareContext);

	function onUpsellExit() {
		if (playerMode === 'paused') {
			onPlay();
		}
		logEvent({
			eventName: 'Screen : Exited',
			eventProps: {
				screen: 'Modal',
				type: 'Payment Screen',
			},
		});
		_onUpsellExit();
	}

	const messageHeading = isFreeTrial ? messages.trialHeading : messages.noTrialheading;
	const showCallout = isFreeTrial;

	useEffect(() => {
		if (shouldShowSharePayment) {
			logEvent({
				eventName: 'Screen : Viewed',
				eventProps: {
					screen: 'Modal',
					type: 'Payment Screen',
				},
			});
		}
	}, [shouldShowSharePayment, logEvent]);

	return (
		<Wrapper isVisible={shouldShowSharePayment}>
			<Header isStatic hideMenu hideProfile onExit={onUpsellExit} />
			<Container activeScreen={true}>
				{showCallout && (
					<div>
						<LimitedTimeCallout>{formatMessage(messages.todayOnly)}</LimitedTimeCallout>
					</div>
				)}
				<Text el="h1" styleAs="h4" color="gray7" weight={FontWeights.Medium}>
					{formatMessage(messageHeading, {
						br: <br />,
					})}
				</Text>
				<PaymentSubHeading size={FontSizes.base} color="gray7" weight={FontWeights.Regular}>
					{formatMessage(messages.subheading)}
				</PaymentSubHeading>
				{shouldShowSharePayment && (
					<CheckoutSessionsPaymentForm
						flowName={SHARE_FREE_CONTENT_FLOW_NAME}
						plan="yearly"
						pageName="player"
						hasFreeTrial={isFreeTrial}
						isCheckoutSessionsEnabled={isCheckoutSessionsEnabled}
						endpoint="subscription"
						afterPurchaseSuccess={onPurchaseSuccess}
						showLegacyPurchaseTerms
					/>
				)}
			</Container>
		</Wrapper>
	);
}

export default SharePayment;
